import {
  Grid,
  TextField,
  Button,
  Typography,
  Avatar,
  IconButton,
  Autocomplete,
  Box,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { PhotoCamera, Delete, PersonOutline, DescriptionOutlined } from "@mui/icons-material";
import dayjs from "dayjs";
import { getIn } from "formik";
import toast from "react-hot-toast";
import {
  BRAND,
  requiredFieldSx,
  primaryButtonSx,
  subsectionHeadingSx,
  requiredAsteriskSx,
  ALLOWED_FILE_ACCEPT,
  FILE_UPLOAD_HELPER_TEXT,
  validateDocumentFile,
  datePickerDesktopModeMediaQuery,
  datePickerLocaleText,
  getDatePickerSlotProps,
} from "../onboardingTheme";

const handleProfileFileSelect = (file, setFieldValue, setFieldTouched) => {
  if (!file) return;
  const result = validateDocumentFile(file);
  if (!result.valid) {
    toast.error(result.message);
    return;
  }
  setFieldValue("profile_image", file);
  setFieldTouched("profile_image", true, false);
};

const isImageFile = (file) => file && file.type && file.type.startsWith("image/");

const maxDateOfBirth = dayjs().subtract(18, "year");
const minDateOfBirth = dayjs().subtract(100, "year");

const genderOptions = [
  { label: "Male", value: "male" },
  { label: "Female", value: "female" },
  { label: "Other", value: "other" },
  { label: "Prefer not to say", value: "prefer_not_to_say" },
];

const PersonalDetailsStep = ({
  values,
  handleChange,
  handleBlur,
  errors,
  touched,
  setFieldValue,
  setFieldTouched,
}) => (
  <Grid container spacing={{ xs: 1.5, sm: 2 }} alignItems="stretch">
    <Grid item xs={12}>
      <Typography sx={subsectionHeadingSx}>Contact & Identity</Typography>
    </Grid>

    <Grid item xs={12} sm={6}>
      <DatePicker
        label="Date of Birth"
        value={values.date_of_birth ? dayjs(values.date_of_birth) : null}
        maxDate={maxDateOfBirth}
        minDate={minDateOfBirth}
        desktopModeMediaQuery={datePickerDesktopModeMediaQuery}
        localeText={datePickerLocaleText}
        onChange={(date) =>
          setFieldValue(
            "date_of_birth",
            date ? dayjs(date).format("YYYY-MM-DD") : ""
          )
        }
        onClose={() => setFieldTouched("date_of_birth", true, false)}
        slotProps={getDatePickerSlotProps({
          required: true,
          error: Boolean(touched.date_of_birth && errors.date_of_birth),
          helperText: touched.date_of_birth && errors.date_of_birth,
        })}
      />
    </Grid>

    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        size="small"
        required
        label="Contact Number"
        name="mobile_number"
        value={values.mobile_number}
        onChange={(e) => {
          const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 10);
          setFieldValue("mobile_number", digitsOnly);
        }}
        onBlur={handleBlur}
        sx={requiredFieldSx}
        inputProps={{ maxLength: 10, inputMode: "numeric" }}
        error={touched.mobile_number && Boolean(errors.mobile_number)}
        helperText={touched.mobile_number && errors.mobile_number}
      />
    </Grid>

    <Grid item xs={12} sm={6}>
      <Autocomplete
        fullWidth
        size="small"
        options={genderOptions}
        getOptionLabel={(option) => option.label}
        value={genderOptions.find((opt) => opt.value === values.gender) || null}
        onChange={(event, newValue) => {
          setFieldValue("gender", newValue ? newValue.value : "");
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Gender"
            name="gender"
            required
            sx={requiredFieldSx}
            error={touched.gender && Boolean(errors.gender)}
            helperText={touched.gender && errors.gender}
          />
        )}
      />
    </Grid>

    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        size="small"
        required
        label="Current Address"
        name="address.currentAddress"
        value={getIn(values, "address.currentAddress") || ""}
        onChange={handleChange}
        onBlur={handleBlur}
        sx={requiredFieldSx}
        error={Boolean(
          getIn(touched, "address.currentAddress") &&
            getIn(errors, "address.currentAddress")
        )}
        helperText={
          getIn(touched, "address.currentAddress") &&
          getIn(errors, "address.currentAddress")
        }
      />
    </Grid>

    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        size="small"
        required
        label="Permanent Address"
        name="address.residentialAddress"
        value={getIn(values, "address.residentialAddress") || ""}
        onChange={handleChange}
        onBlur={handleBlur}
        sx={requiredFieldSx}
        error={Boolean(
          getIn(touched, "address.residentialAddress") &&
            getIn(errors, "address.residentialAddress")
        )}
        helperText={
          getIn(touched, "address.residentialAddress") &&
          getIn(errors, "address.residentialAddress")
        }
      />
    </Grid>

    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        size="small"
        required
        label="Aadhar Number"
        name="aadhar_number"
        value={values.aadhar_number || ""}
        onChange={(e) => {
          const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 12);
          setFieldValue("aadhar_number", digitsOnly);
        }}
        onBlur={handleBlur}
        sx={requiredFieldSx}
        inputProps={{ maxLength: 12 }}
        error={touched.aadhar_number && Boolean(errors.aadhar_number)}
        helperText={touched.aadhar_number && errors.aadhar_number}
      />
    </Grid>

    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        size="small"
        required
        label="Nationality"
        name="nationality"
        value={values.nationality}
        onChange={(e) => {
          const lettersOnly = e.target.value.replace(/[^A-Za-z\s]/g, "");
          setFieldValue("nationality", lettersOnly);
        }}
        onBlur={handleBlur}
        sx={requiredFieldSx}
        error={touched.nationality && Boolean(errors.nationality)}
        helperText={touched.nationality && errors.nationality}
      />
    </Grid>

    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        size="small"
        label="Alternative Contact Number"
        name="alternative_mobile_number"
        value={values.alternative_mobile_number}
        onChange={(e) => {
          const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 10);
          setFieldValue("alternative_mobile_number", digitsOnly);
        }}
        onBlur={handleBlur}
        sx={requiredFieldSx}
        inputProps={{ maxLength: 10, inputMode: "numeric" }}
        error={
          touched.alternative_mobile_number &&
          Boolean(errors.alternative_mobile_number)
        }
        helperText={
          touched.alternative_mobile_number && errors.alternative_mobile_number
        }
      />
    </Grid>

    <Grid
      item
      xs={12}
      sm={6}
      sx={{ display: "flex", flexDirection: "column", alignSelf: "stretch" }}
    >
      <TextField
        fullWidth
        size="small"
        name="about"
        label="Profile Summary"
        placeholder="Briefly describe your background, skills, or interests"
        value={getIn(values, "about") || ""}
        onChange={handleChange}
        onBlur={handleBlur}
        sx={{
          ...requiredFieldSx,
          flex: 1,
          "& .MuiInputBase-root": {
            alignItems: "flex-start",
            minHeight: 120,
          },
        }}
        error={Boolean(getIn(touched, "about") && getIn(errors, "about"))}
        helperText={getIn(touched, "about") && getIn(errors, "about")}
        multiline
        rows={3}
      />
    </Grid>

    <Grid
      item
      xs={12}
      sm={6}
      sx={{ display: "flex", flexDirection: "column", alignSelf: "stretch" }}
    >
      <Typography
        variant="body2"
        sx={{
          color: "rgba(0, 0, 0, 0.6)",
          fontSize: "0.875rem",
          mb: 0.75,
          lineHeight: 1.2,
        }}
      >
        Profile Photo
        <Box component="span" sx={requiredAsteriskSx}>
          *
        </Box>
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: { xs: 1.5, sm: 2 },
          flex: 1,
          minHeight: { xs: "auto", sm: 120 },
          p: { xs: 1.25, sm: 1.5 },
          borderRadius: "12px",
          border: `1px solid ${BRAND.border}`,
          bgcolor: BRAND.bg,
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "inline-flex",
            flexShrink: 0,
            alignSelf: { xs: "center", sm: "flex-start" },
          }}
        >
          <Avatar
            src={
              values.profile_image && isImageFile(values.profile_image)
                ? URL.createObjectURL(values.profile_image)
                : undefined
            }
            alt="Profile Preview"
            sx={{
              width: 72,
              height: 72,
              border: `2px solid ${values.profile_image ? BRAND.orange : BRAND.border}`,
              bgcolor: "#fff",
            }}
          >
            {!values.profile_image && (
              <PersonOutline sx={{ fontSize: 36, color: BRAND.muted }} />
            )}
            {values.profile_image && !isImageFile(values.profile_image) && (
              <DescriptionOutlined sx={{ fontSize: 36, color: BRAND.orange }} />
            )}
          </Avatar>
          {values.profile_image && (
            <IconButton
              size="small"
              aria-label="Remove profile image"
              sx={{
                position: "absolute",
                top: -4,
                right: -4,
                bgcolor: "#fff",
                border: `1px solid ${BRAND.border}`,
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                "&:hover": { bgcolor: "#FEE2E2" },
              }}
              onClick={() => setFieldValue("profile_image", null)}
            >
              <Delete fontSize="small" sx={{ color: "#EF4444" }} />
            </IconButton>
          )}
        </Box>

        <Box sx={{ minWidth: 0, width: "100%" }}>
          <Typography
            variant="caption"
            sx={{ color: BRAND.muted, mb: 1, display: "block" }}
          >
            {FILE_UPLOAD_HELPER_TEXT}
          </Typography>
          <Button
            variant="contained"
            disableElevation
            component="label"
            size="small"
            fullWidth
            startIcon={<PhotoCamera />}
            sx={{
              ...primaryButtonSx,
              py: { xs: 1, sm: 0.5 },
              px: 2,
              minHeight: 44,
            }}
          >
            {values.profile_image ? "Change Photo" : "Upload Photo"}
            <input
              hidden
              type="file"
              accept={ALLOWED_FILE_ACCEPT}
              onChange={(event) => {
                const file = event.currentTarget.files?.[0];
                event.currentTarget.value = "";
                handleProfileFileSelect(file, setFieldValue, setFieldTouched);
              }}
            />
          </Button>

          {values.profile_image && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5, color: BRAND.muted }}>
              {values.profile_image.name}
            </Typography>
          )}

          {touched.profile_image && errors.profile_image && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
              {errors.profile_image}
            </Typography>
          )}
        </Box>
      </Box>
    </Grid>
  </Grid>
);

export default PersonalDetailsStep;
